import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Video, XIcon } from 'lucide-react'
import type { MediaItem } from '../../types'

const variants = {
  enter:  (d: number) => ({ x: d > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] as const } },
  exit:   (d: number) => ({ x: d > 0 ? '-100%' : '100%', opacity: 0, transition: { duration: 0.25 } }),
}

interface Props {
  media: MediaItem[]
  name: string
}

export default function Carousel({ media, name }: Props) {
  const [current, setCurrent]     = useState(0)
  const [direction, setDirection] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const go = (dir: number) => {
    setDirection(dir)
    setCurrent(i => (i + dir + media.length) % media.length)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')  go(1)
      if (e.key === 'ArrowRight') go(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [media.length])

  if (media.length === 0) return (
    <div className="h-[320px] flex items-center justify-center text-[116px] bg-[linear-gradient(135deg,#FAF7F2_0%,#FAF7F2_70%,rgba(244,132,95,0.15)_100%)]">
      📦
    </div>
  )

  const item = media[current]

  return (
    <>
    <AnimatePresence>
      {lightboxOpen && item.type === 'image' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center cursor-zoom-out"
        >
          <motion.img
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.9 }}
            src={item.src}
            alt={name}
            onClick={e => e.stopPropagation()}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
          />
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center border-none cursor-pointer text-white"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
      <div className="relative h-[320px] overflow-hidden bg-[#F2EDE4] group">
        <AnimatePresence custom={direction} mode="popLayout">
          {item.type === 'video' ? (
            <motion.div
              key={current}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0 bg-black flex items-center justify-center"
            >
              <video
                src={item.src}
                controls
                className="w-full h-full object-contain"
                aria-label={`${name} - וידאו ${current + 1}`}
              />
            </motion.div>
          ) : (
            <motion.img
              key={current}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              src={item.src}
              alt={`${name} - תמונה ${current + 1}`}
              className="absolute inset-0 w-full h-full object-contain cursor-zoom-in"
              draggable={false}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={(_, info) => {
                if (info.offset.x < -50) go(1)
                else if (info.offset.x > 50) go(-1)
              }}
            onClick={() => setLightboxOpen(true)}
            />
          )}
        </AnimatePresence>

        {item.type === 'image' && (
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
        )}

        {media.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              aria-label="פריט קודם"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm border-none cursor-pointer flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.15)] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronRight className="h-4 w-4 text-[#1A1A1A]" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="פריט הבא"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm border-none cursor-pointer flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.15)] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronLeft className="h-4 w-4 text-[#1A1A1A]" />
            </button>

            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 items-center">
              {media.map((m, i) => (
                <button
                  key={i}
                  onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i) }}
                  aria-label={`${m.type === 'video' ? 'וידאו' : 'תמונה'} ${i + 1}`}
                  className={`rounded-full border-none cursor-pointer transition-all ${
                    i === current ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {media.length > 1 && (
          <span className="absolute top-3 right-3 bg-black/40 backdrop-blur-sm text-white text-[11px] font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
            {item.type === 'video' && <Video className="w-3 h-3" />}
            {current + 1} / {media.length}
          </span>
        )}
      </div>
      {media.length > 1 && (
        <div className="flex gap-2 px-8 py-3 overflow-x-auto scrollbar-hide lg:scrollbar-default">
          {media.map((m, i) => (
            <button
              key={i}
              onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i) }}
              className={`shrink-0 w-12 h-12 rounded-[8px] overflow-hidden border-2 transition-all cursor-pointer
                ${i === current ? 'border-green-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}
            >
              {m.type === 'video'
                ? <div className="w-full h-full bg-black flex items-center justify-center">
                    <Video className="w-4 h-4 text-white" />
                  </div>
                : <img src={m.src} alt={`תמונה ${i + 1}`} className="w-full h-full object-cover" />
              }
            </button>
          ))}
        </div>
      )}
      {/* <button
        onClick={() => toggleFavorite(productId)}
        aria-label={isFavorite(productId) ? 'הסר ממועדפים' : 'הוסף למועדפים'}
        aria-pressed={isFavorite(productId)}
        className={`absolute bottom-4 lg:bottom-4 left-8 z-10 w-10 h-10 rounded-full flex items-center justify-center border-none cursor-pointer transition-all duration-200
          ${isFavorite(productId)
            ? 'bg-[#E74C3C] shadow-[0_2px_8px_rgba(231,76,60,0.4)]'
            : 'bg-cream hover:bg-cream-dark shadow-[0_2px_6px_rgba(44,24,16,0.10)]'
          }`}
      >
        <Heart className={`w-5 h-5 transition-all duration-200 ${isFavorite(productId) ? 'fill-white text-white scale-110' : 'text-[#A89F97]'}`} />
      </button> */}
    </>
  )
}